/**
 * Digital Ticket/Pass Component
 * Generates downloadable ticket for mobile devices
 * Supports: Image download, Apple Wallet (pkpass), Google Wallet
 */
import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './button';
import { Card } from './card';
import { 
  Download, Wallet, Calendar, MapPin, Clock, 
  User, Ticket, QrCode, Share2, Check
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DigitalTicket = ({
  registration,
  event,
  tier,
  className,
  variant = 'full' // 'full' | 'compact'
}) => {
  const ticketRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // QR payload for the ticket — real, scannable QR rendered locally
  const getQRPayload = () => {
    return registration?.qr_data || registration?.confirmation_code || registration?.id || 'TICKET';
  };

  // Download ticket as image
  const downloadAsImage = async () => {
    setDownloading(true);
    try {
      // Use html2canvas if available, otherwise create a simple image
      const element = ticketRef.current;
      if (!element) {
        toast.error('Unable to generate ticket');
        return;
      }

      // Try to use native share if available (mobile)
      if (navigator.share && navigator.canShare) {
        try {
          await navigator.share({
            title: `${event?.name} - Ticket`,
            text: `Your ticket for ${event?.name}\nConfirmation: ${registration?.confirmation_code}`,
            url: window.location.href
          });
          toast.success('Ticket shared!');
          return;
        } catch (err) {
          // User cancelled or share failed, continue to download
        }
      }

      // Fallback: Download ticket info as text file
      const ticketInfo = `
═══════════════════════════════════
       ${event?.name || 'Event Ticket'}
═══════════════════════════════════

CONFIRMATION CODE: ${registration?.confirmation_code || 'N/A'}

ATTENDEE: ${registration?.first_name} ${registration?.last_name}
EMAIL: ${registration?.email}
TICKET TYPE: ${tier?.name || 'General Admission'}

DATE: ${formatDate(event?.start_date)}
TIME: ${formatTime(event?.start_date)}
VENUE: ${event?.venue || 'See event details'}

═══════════════════════════════════
         Present this ticket at entry
═══════════════════════════════════
      `;

      const blob = new Blob([ticketInfo], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${registration?.confirmation_code || 'event'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Ticket downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download ticket');
    } finally {
      setDownloading(false);
    }
  };

  // Add to calendar
  const addToCalendar = () => {
    const startDate = event?.start_date ? new Date(event.start_date) : new Date();
    const endDate = event?.end_date ? new Date(event.end_date) : new Date(startDate.getTime() + 3600000);
    
    const formatCalDate = (d) => d.toISOString().replace(/-|:|\.\d+/g, '');
    
    const calUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(event?.name || 'Event')}` +
      `&dates=${formatCalDate(startDate)}/${formatCalDate(endDate)}` +
      `&details=${encodeURIComponent(`Confirmation: ${registration?.confirmation_code}\nTicket: ${tier?.name}`)}` +
      `&location=${encodeURIComponent(event?.venue || '')}`;
    
    window.open(calUrl, '_blank');
    toast.success('Opening calendar...');
  };

  // Copy confirmation code
  const copyCode = () => {
    navigator.clipboard.writeText(registration?.confirmation_code || '');
    setCopied(true);
    toast.success('Confirmation code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (variant === 'compact') {
    return (
      <Card className={cn("p-4 bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700", className)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Ticket className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-white font-medium">{tier?.name || 'Ticket'}</p>
              <p className="text-slate-400 text-sm">{registration?.confirmation_code}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={addToCalendar}>
              <Calendar className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={downloadAsImage}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className={cn("max-w-md mx-auto", className)}>
      {/* Ticket Card */}
      <div 
        ref={ticketRef}
        className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-700"
      >
        {/* Header */}
        <div 
          className="p-6 text-center"
          style={{ background: `linear-gradient(135deg, ${event?.branding?.primary_color || '#059669'}, ${event?.branding?.secondary_color || '#0d9488'})` }}
        >
          <h2 className="text-2xl font-bold text-white mb-1">{event?.name}</h2>
          <p className="text-white/80">{tier?.name || 'General Admission'}</p>
        </div>

        {/* Ticket perforation line */}
        <div className="relative">
          <div className="absolute left-0 w-6 h-6 bg-slate-950 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute right-0 w-6 h-6 bg-slate-950 rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="border-t-2 border-dashed border-slate-600 mx-6" />
        </div>

        {/* Details */}
        <div className="p-6 space-y-4">
          {/* Attendee */}
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wide">Attendee</p>
              <p className="text-white font-medium">{registration?.first_name} {registration?.last_name}</p>
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wide">Date & Time</p>
              <p className="text-white font-medium">{formatDate(event?.start_date)}</p>
              {event?.start_date && (
                <p className="text-slate-300 text-sm">{formatTime(event?.start_date)}</p>
              )}
            </div>
          </div>

          {/* Location */}
          {event?.venue && (
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wide">Venue</p>
                <p className="text-white font-medium">{event?.venue}</p>
              </div>
            </div>
          )}

          {/* QR Code & Confirmation */}
          <div className="mt-6 pt-6 border-t border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Confirmation Code</p>
                <button 
                  onClick={copyCode}
                  className="flex items-center gap-2 text-2xl font-mono font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  {registration?.confirmation_code || 'PENDING'}
                  {copied ? <Check className="h-5 w-5" /> : <Share2 className="h-4 w-4 opacity-50" />}
                </button>
              </div>
              <div className="bg-white p-2 rounded-lg" data-testid="digital-ticket-qr">
                <QRCodeSVG
                  value={String(getQRPayload())}
                  size={80}
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 text-center">
          <p className="text-slate-500 text-xs">
            Present this ticket at entry • Powered by EventFlow
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-4 flex gap-3">
        <Button 
          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          onClick={downloadAsImage}
          disabled={downloading}
        >
          <Download className="h-4 w-4 mr-2" />
          {downloading ? 'Downloading...' : 'Download Ticket'}
        </Button>
        <Button 
          variant="outline" 
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
          onClick={addToCalendar}
        >
          <Calendar className="h-4 w-4 mr-2" />
          Add to Calendar
        </Button>
      </div>

      {/* Mobile install hint */}
      <p className="mt-4 text-center text-slate-500 text-sm">
        📱 Save this page to your home screen for quick access
      </p>
    </div>
  );
};

export default DigitalTicket;
